import React from 'react'
import { Button, Typography } from '@s4l/ds'
import { useS4LContext } from '@s4l/react-context'
import styles from './styles.module.css'
import type { BookingInformation } from '../types'
import { formatGuests } from './utils'
import DateIntl from 'src/comps/DateIntl'
import { DATE_INTL_FORMAT_WEEKDAY_MONTH_TEXT } from 'src/comps/DateIntl/constants'
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays'
import { IconEdit } from '@s4l/icons/IconEdit'
import Price from 'client/components/Price'
import cn from 'classnames'
import type { UtilityClassNames } from '@s4l/utility-css'

const ModifyDatesDetails = ({
  booking,
  newBooking,
  title,
  subtitle,
  onClick,
  total = false,
  totalFrom,
  border = false,
  className,
}: {
  booking: BookingInformation
  newBooking: BookingInformation
  title?: string
  subtitle?: string
  onClick?: () => void
  total?: boolean
  totalFrom?: number
  border?: boolean
  className?: UtilityClassNames
}) => {
  const { i18n } = useS4LContext()

  const guests = formatGuests(booking?.adults, booking?.children, i18n, booking?.rooms ?? 0)
  const newGuests = newBooking?.adults
    ? formatGuests(newBooking?.adults, newBooking?.children, i18n, newBooking?.rooms ?? 0)
    : null

  const nights = booking?.nights
    ? booking.nights
    : differenceInCalendarDays(new Date(booking?.checkOut), new Date(booking?.checkIn))

  const newNights =
    newBooking?.checkOut && newBooking?.checkIn
      ? differenceInCalendarDays(new Date(newBooking.checkOut), new Date(newBooking.checkIn))
      : null

  return (
    <section className={cn(styles.wrapper, { [styles.border]: border }, className)}>
      <div className={styles.header}>
        {title ? (
          <Typography variant="body" size="xl" bold>
            {i18n.formatMessage({ id: title })}
          </Typography>
        ) : null}

        {subtitle ? (
          <Typography variant="body" size="l">
            {i18n.formatMessage({ id: subtitle })}
          </Typography>
        ) : null}

        {onClick && (
          <Button
            as="button"
            onClick={onClick}
            variant="text-flat"
            leadingIcon={<IconEdit size={16} />}
            size="small"
          >
            {i18n.formatMessage({ id: 'global.modify' })}
          </Button>
        )}
      </div>

      <div className={styles.grid}>
        <div className={styles.label}>
          <Typography variant="body" size="l" bold>
            {i18n.formatMessage({ id: 'change-dates.arrival' })}
          </Typography>
        </div>
        <div className={styles.value}>
          {booking?.checkIn === newBooking?.checkIn || !newBooking?.checkIn ? (
            <Typography variant="body" size="l">
              <DateIntl value={booking?.checkIn} format={DATE_INTL_FORMAT_WEEKDAY_MONTH_TEXT} />
            </Typography>
          ) : (
            <>
              <Typography variant="body" size="l" strike>
                <DateIntl value={booking?.checkIn} format={DATE_INTL_FORMAT_WEEKDAY_MONTH_TEXT} />
              </Typography>
              <Typography variant="body" size="l" bold>
                <DateIntl
                  value={newBooking?.checkIn}
                  format={DATE_INTL_FORMAT_WEEKDAY_MONTH_TEXT}
                />
              </Typography>
            </>
          )}
        </div>
        <div className={styles.label}>
          <Typography variant="body" size="l" bold>
            {i18n.formatMessage({ id: 'change-dates.exit' })}
          </Typography>
        </div>
        <div className={styles.value}>
          {booking?.checkOut === newBooking?.checkOut || !newBooking?.checkOut ? (
            <Typography variant="body" size="l">
              <DateIntl value={booking?.checkOut} format={DATE_INTL_FORMAT_WEEKDAY_MONTH_TEXT} />
            </Typography>
          ) : (
            <>
              <Typography variant="body" size="l" strike>
                <DateIntl value={booking?.checkOut} format={DATE_INTL_FORMAT_WEEKDAY_MONTH_TEXT} />
              </Typography>
              <Typography variant="body" size="l" bold>
                <DateIntl
                  value={newBooking?.checkOut}
                  format={DATE_INTL_FORMAT_WEEKDAY_MONTH_TEXT}
                />
              </Typography>
            </>
          )}
        </div>
        <div className={styles.label}>
          <Typography variant="body" size="l" bold>
            {i18n.formatMessage({ id: 'change-dates.stay' })}
          </Typography>
        </div>
        <div className={styles.value}>
          {nights === newNights || !newNights ? (
            <Typography variant="body" size="l">
              {i18n.formatMessage({ id: 'global.nights' }, { nights })}
            </Typography>
          ) : (
            <>
              <Typography variant="body" size="l" strike>
                {i18n.formatMessage({ id: 'global.nights' }, { nights })}
              </Typography>
              <Typography variant="body" size="l" bold>
                {i18n.formatMessage({ id: 'global.nights' }, { nights: newNights })}
              </Typography>
            </>
          )}
        </div>
        <div className={styles.label}>
          <Typography variant="body" size="l" bold>
            {i18n.formatMessage({ id: 'change-dates.guests' })}
          </Typography>
        </div>
        <div className={styles.value}>
          {guests === newGuests || !newGuests ? (
            <Typography variant="body" size="l">
              {guests}
            </Typography>
          ) : (
            <>
              <Typography variant="body" size="l" strike>
                {guests}
              </Typography>
              <Typography variant="body" size="l" bold>
                {newGuests}
              </Typography>
            </>
          )}
        </div>
        {total && booking?.sellingRate !== null && booking?.currency && (
          <>
            <div className={styles.label}>
              <Typography variant="body" size="l" bold>
                {i18n.formatMessage({ id: 'global.total' })}
              </Typography>
            </div>
            <div className={styles.value}>
              <Typography variant="title" size="xs" bold>
                <Price amount={booking?.sellingRate} currency={booking?.currency} />
              </Typography>
            </div>
          </>
        )}
        {totalFrom && booking?.currency && (
          <>
            <div className={styles.label}>
              <Typography variant="body" size="l" bold>
                {i18n.formatMessage({ id: 'global.total' })}
              </Typography>
            </div>
            <div className={styles.totalValue}>
              <Typography variant="body" size="l">
                {i18n.formatMessage({ id: 'global.from-intro' })}
              </Typography>
              <Typography variant="title" size="xs" bold>
                <Price amount={totalFrom} currency={booking?.currency} />
              </Typography>
            </div>
          </>
        )}
      </div>
    </section>
  )
}

export default ModifyDatesDetails
